import React, { useState, useRef, useEffect } from 'react';
import { DeviceSettings, SettingsUpdate, TrafficRoute, CreditCard } from '../types';
import { PopupContainer, usePopup } from './common';
import './SettingsPanel.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5002';

interface LocationPrediction {
  description: string;
  place_id: string;
}

interface SettingsPanelProps {
  settings: DeviceSettings;
  onSave: (updates: SettingsUpdate) => Promise<void>;
  onSendSummary: () => Promise<void>;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ settings, onSave, onSendSummary }) => {
  const [activeTab, setActiveTab] = useState<'device' | 'weather' | 'traffic' | 'cards' | 'quotes' | 'summary'>('device');
  const [formData, setFormData] = useState<DeviceSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  // Popup system
  const { popup, closePopup, showSuccess, showError } = usePopup();
  const [locationQuery, setLocationQuery] = useState('');
  const [locationPredictions, setLocationPredictions] = useState<LocationPrediction[]>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Traffic route autocomplete state
  const [trafficSearchQuery, setTrafficSearchQuery] = useState<{[key: string]: string}>({});
  const [trafficPredictions, setTrafficPredictions] = useState<LocationPrediction[]>([]);
  const [showTrafficDropdown, setShowTrafficDropdown] = useState<{routeIndex: number, field: 'origin' | 'destination'} | null>(null);
  const [searchingTraffic, setSearchingTraffic] = useState(false);
  const trafficSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Search for locations with debounce
  useEffect(() => {
    if (locationQuery.length < 2) {
      setLocationPredictions([]);
      setShowLocationDropdown(false);
      return;
    }

    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingLocation(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/location/search?query=${encodeURIComponent(locationQuery)}&location_type=cities&country=mx`
        );

        if (response.ok) {
          const data = await response.json();
          setLocationPredictions(data.predictions || []);
          setShowLocationDropdown(data.predictions.length > 0);
        }
      } catch (error) {
        console.error('Error searching locations:', error);
      } finally {
        setSearchingLocation(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [locationQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        locationInputRef.current &&
        !locationInputRef.current.contains(event.target as Node)
      ) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle location selection
  const handleLocationSelect = async (prediction: LocationPrediction) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/location/details?place_id=${encodeURIComponent(prediction.place_id)}`
      );

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, weather_location: data.formatted_address || prediction.description });
        setLocationQuery(data.formatted_address || prediction.description);
        setShowLocationDropdown(false);
      }
    } catch (error) {
      console.error('Error getting location details:', error);
      setFormData({ ...formData, weather_location: prediction.description });
      setLocationQuery(prediction.description);
      setShowLocationDropdown(false);
    }
  };

  // Initialize location query from settings
  useEffect(() => {
    if (settings.weather_location) {
      setLocationQuery(settings.weather_location);
    }
  }, [settings.weather_location]);

  // Initialize traffic search queries from existing routes
  useEffect(() => {
    const queries: {[key: string]: string} = {};
    formData.traffic_routes.forEach((route, index) => {
      if (route.origin) {
        queries[`${index}-origin`] = route.origin;
      }
      if (route.destination) {
        queries[`${index}-destination`] = route.destination;
      }
    });
    setTrafficSearchQuery(queries);
  }, [formData.traffic_routes.length]);

  // Search for traffic locations with debounce
  useEffect(() => {
    const activeQuery = showTrafficDropdown
      ? trafficSearchQuery[`${showTrafficDropdown.routeIndex}-${showTrafficDropdown.field}`]
      : '';

    if (!activeQuery || activeQuery.length < 2) {
      setTrafficPredictions([]);
      return;
    }

    if (trafficSearchTimeoutRef.current !== null) {
      clearTimeout(trafficSearchTimeoutRef.current);
    }

    trafficSearchTimeoutRef.current = setTimeout(async () => {
      setSearchingTraffic(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/location/search?query=${encodeURIComponent(activeQuery)}&location_type=address&country=mx`
        );

        if (response.ok) {
          const data = await response.json();
          setTrafficPredictions(data.predictions || []);
        }
      } catch (error) {
        console.error('Error searching traffic locations:', error);
      } finally {
        setSearchingTraffic(false);
      }
    }, 300);

    return () => {
      if (trafficSearchTimeoutRef.current !== null) {
        clearTimeout(trafficSearchTimeoutRef.current);
      }
    };
  }, [trafficSearchQuery, showTrafficDropdown]);

  // Handle traffic location selection
  const handleTrafficLocationSelect = async (prediction: LocationPrediction, routeIndex: number, field: 'origin' | 'destination') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/location/details?place_id=${encodeURIComponent(prediction.place_id)}`
      );

      if (response.ok) {
        const data = await response.json();
        const routes = [...formData.traffic_routes];

        if (field === 'origin') {
          routes[routeIndex] = {
            ...routes[routeIndex],
            origin: data.formatted_address || prediction.description,
            origin_lat: data.latitude,
            origin_lng: data.longitude
          };
        } else {
          routes[routeIndex] = {
            ...routes[routeIndex],
            destination: data.formatted_address || prediction.description,
            destination_lat: data.latitude,
            destination_lng: data.longitude
          };
        }

        setFormData({ ...formData, traffic_routes: routes });
        setTrafficSearchQuery({
          ...trafficSearchQuery,
          [`${routeIndex}-${field}`]: data.formatted_address || prediction.description
        });
        setShowTrafficDropdown(null);
      }
    } catch (error) {
      console.error('Error getting traffic location details:', error);
      const routes = [...formData.traffic_routes];
      routes[routeIndex] = {
        ...routes[routeIndex],
        [field]: prediction.description
      };
      setFormData({ ...formData, traffic_routes: routes });
      setTrafficSearchQuery({
        ...trafficSearchQuery,
        [`${routeIndex}-${field}`]: prediction.description
      });
      setShowTrafficDropdown(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData as SettingsUpdate);
      showSuccess('Success!', 'Your settings have been saved successfully.');
    } catch (error) {
      showError('Error!', 'Failed to save settings. Please try again.');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendSummaryNow = async () => {
    setSendingSummary(true);
    try {
      await onSendSummary();
      showSuccess('Email Sent!', 'Daily summary has been sent to your email. Check your inbox.');
    } catch (error) {
      showError('Error!', 'Failed to send summary email. Please try again.');
      console.error(error);
    } finally {
      setSendingSummary(false);
    }
  };

  const addTrafficRoute = () => {
    setFormData({
      ...formData,
      traffic_routes: [...formData.traffic_routes, { origin: '', destination: '', name: '' }],
    });
  };

  const updateTrafficRoute = (index: number, field: keyof TrafficRoute, value: string) => {
    const routes = [...formData.traffic_routes];
    routes[index] = { ...routes[index], [field]: value };
    setFormData({ ...formData, traffic_routes: routes });
  };

  const removeTrafficRoute = (index: number) => {
    setFormData({
      ...formData,
      traffic_routes: formData.traffic_routes.filter((_, i) => i !== index),
    });
  };

  const addCreditCard = () => {
    setFormData({
      ...formData,
      credit_cards: [
        ...formData.credit_cards,
        { card_name: '', last4: '', due_date: '', amount: 0, minimum_payment: 0 },
      ],
    });
  };

  const updateCreditCard = (index: number, field: keyof CreditCard, value: string | number) => {
    const cards = [...formData.credit_cards];
    cards[index] = { ...cards[index], [field]: value };
    setFormData({ ...formData, credit_cards: cards });
  };

  const removeCreditCard = (index: number) => {
    setFormData({
      ...formData,
      credit_cards: formData.credit_cards.filter((_, i) => i !== index),
    });
  };

  return (
    <>
    <div className="settings-panel">
      <div className="settings-header">
        <div className="settings-title">
          <svg className="settings-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 1V5M12 19V23M23 12H19M5 12H1M19.778 4.222L16.95 7.05M7.05 16.95L4.222 19.778M19.778 19.778L16.95 16.95M7.05 7.05L4.222 4.222" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h3>Device Configuration</h3>
        </div>
        <button className="btn-save" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <svg className="btn-icon spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M17 21V13H7V21M7 3V8H15" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Save Settings
            </>
          )}
        </button>
      </div>

      <div className="settings-tabs">
        <button
          className={`tab ${activeTab === 'device' ? 'active' : ''}`}
          onClick={() => setActiveTab('device')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 1V5M12 19V23M23 12H19M5 12H1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Device
        </button>
        <button
          className={`tab ${activeTab === 'weather' ? 'active' : ''}`}
          onClick={() => setActiveTab('weather')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 1V3M12 21V23M23 12H21M3 12H1M20.49 3.51L19.07 4.93M4.93 19.07L3.51 20.49M20.49 20.49L19.07 19.07M4.93 4.93L3.51 3.51" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Weather
        </button>
        <button
          className={`tab ${activeTab === 'traffic' ? 'active' : ''}`}
          onClick={() => setActiveTab('traffic')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M19 17H5M19 17C20.6569 17 22 18.3431 22 20C22 21.6569 20.6569 23 19 23C17.3431 23 16 21.6569 16 20M19 17V3M5 17C3.34315 17 2 18.3431 2 20C2 21.6569 3.34315 23 5 23C6.65685 23 8 21.6569 8 20M5 17V7" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Traffic
        </button>
        <button
          className={`tab ${activeTab === 'cards' ? 'active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Credit Cards
        </button>
        <button
          className={`tab ${activeTab === 'quotes' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotes')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M7 8C7 7.46957 7.21071 6.96086 7.58579 6.58579C7.96086 6.21071 8.46957 6 9 6H11C11.5304 6 12.0391 6.21071 12.4142 6.58579C12.7893 6.96086 13 7.46957 13 8V10H7V8Z" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 14C15 13.4696 15.2107 12.9609 15.5858 12.5858C15.9609 12.2107 16.4696 12 17 12H19C19.5304 12 20.0391 12.2107 20.4142 12.5858C20.7893 12.9609 21 13.4696 21 14V16H15V14Z" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Quotes
        </button>
        <button
          className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M3 9L12 14L21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Summary
        </button>
      </div>

      <div className="settings-content">
        {/* Device Tab */}
        {activeTab === 'device' && (
          <div className="settings-section">
            <h4>Device Configuration</h4>
            <div className="form-group">
              <label>Device Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="InkSpire E-Paper Display"
              />
            </div>
            <div className="form-group">
              <label>WiFi SSID</label>
              <input
                type="text"
                value={formData.wifi_ssid}
                onChange={(e) => setFormData({ ...formData, wifi_ssid: e.target.value })}
                placeholder="Your WiFi Network Name"
              />
              <small>Device will connect to this WiFi network</small>
            </div>
            <div className="form-group">
              <label>WiFi Password</label>
              <input
                type="password"
                value={formData.wifi_password}
                onChange={(e) => setFormData({ ...formData, wifi_password: e.target.value })}
                placeholder="WiFi Password"
              />
              <small>Stored securely and used only by the device</small>
            </div>
            <div className="form-group">
              <label>Screen Switch Interval (seconds)</label>
              <input
                type="number"
                value={formData.screen_switch_interval}
                onChange={(e) =>
                  setFormData({ ...formData, screen_switch_interval: parseInt(e.target.value) })
                }
                min="10"
                max="300"
              />
              <small>Time between automatic screen transitions (10-300 seconds)</small>
            </div>
          </div>
        )}

        {/* Weather Tab */}
        {activeTab === 'weather' && (
          <div className="settings-section">
            <h4>Weather Configuration</h4>
            <div className="form-group">
              <label>Location</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={locationInputRef}
                  type="text"
                  value={locationQuery}
                  onChange={(e) => {
                    setLocationQuery(e.target.value);
                    setFormData({ ...formData, weather_location: e.target.value });
                  }}
                  onFocus={() => {
                    if (locationPredictions.length > 0) {
                      setShowLocationDropdown(true);
                    }
                  }}
                  placeholder="Search for a city or location..."
                  className="location-autocomplete-input"
                />
                {searchingLocation && (
                  <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>
                    <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
                {showLocationDropdown && locationPredictions.length > 0 && (
                  <div ref={dropdownRef} className="location-dropdown">
                    {locationPredictions.map((prediction, index) => (
                      <div
                        key={prediction.place_id}
                        className="location-dropdown-item"
                        onClick={() => handleLocationSelect(prediction)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{prediction.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <small>Start typing to search for locations. Select from suggestions to get accurate coordinates.</small>
            </div>
            <div className="form-group">
              <label>Temperature Unit</label>
              <select
                value={formData.weather_units}
                onChange={(e) =>
                  setFormData({ ...formData, weather_units: e.target.value as 'F' | 'C' })
                }
              >
                <option value="F">Fahrenheit (°F)</option>
                <option value="C">Celsius (°C)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Refresh Interval (minutes)</label>
              <input
                type="number"
                value={formData.weather_refresh_minutes}
                onChange={(e) =>
                  setFormData({ ...formData, weather_refresh_minutes: parseInt(e.target.value) })
                }
                min="10"
                max="120"
              />
            </div>
          </div>
        )}

        {/* Traffic Tab */}
        {activeTab === 'traffic' && (
          <div className="settings-section">
            <div className="section-header">
              <h4>Traffic Routes (Max 5)</h4>
              <button
                className="btn-add"
                onClick={addTrafficRoute}
                disabled={formData.traffic_routes.length >= 5}
              >
                <svg className="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Route
              </button>
            </div>
            {formData.traffic_routes.map((route, index) => (
              <div key={index} className="route-item">
                <div className="route-header">
                  <span>Route {index + 1}</span>
                  <button className="btn-remove" onClick={() => removeTrafficRoute(index)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                <div className="form-group">
                  <label>Name (optional)</label>
                  <input
                    type="text"
                    value={route.name || ''}
                    onChange={(e) => updateTrafficRoute(index, 'name', e.target.value)}
                    placeholder="Home to Work"
                  />
                </div>
                <div className="form-group">
                  <label>Origin Address</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={trafficSearchQuery[`${index}-origin`] || route.origin || ''}
                      onChange={(e) => {
                        setTrafficSearchQuery({
                          ...trafficSearchQuery,
                          [`${index}-origin`]: e.target.value
                        });
                        updateTrafficRoute(index, 'origin', e.target.value);
                      }}
                      onFocus={() => setShowTrafficDropdown({routeIndex: index, field: 'origin'})}
                      placeholder="Search for origin address..."
                      className="location-autocomplete-input"
                    />
                    {searchingTraffic && showTrafficDropdown?.routeIndex === index && showTrafficDropdown?.field === 'origin' && (
                      <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>
                        <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    )}
                    {showTrafficDropdown?.routeIndex === index && showTrafficDropdown?.field === 'origin' && trafficPredictions.length > 0 && (
                      <div className="location-dropdown">
                        {trafficPredictions.map((prediction) => (
                          <div
                            key={prediction.place_id}
                            className="location-dropdown-item"
                            onClick={() => handleTrafficLocationSelect(prediction, index, 'origin')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                              <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span>{prediction.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <small>Coordinates will be saved: {route.origin_lat && route.origin_lng ? `${route.origin_lat.toFixed(4)}, ${route.origin_lng.toFixed(4)}` : 'Not set'}</small>
                </div>
                <div className="form-group">
                  <label>Destination Address</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={trafficSearchQuery[`${index}-destination`] || route.destination || ''}
                      onChange={(e) => {
                        setTrafficSearchQuery({
                          ...trafficSearchQuery,
                          [`${index}-destination`]: e.target.value
                        });
                        updateTrafficRoute(index, 'destination', e.target.value);
                      }}
                      onFocus={() => setShowTrafficDropdown({routeIndex: index, field: 'destination'})}
                      placeholder="Search for destination address..."
                      className="location-autocomplete-input"
                    />
                    {searchingTraffic && showTrafficDropdown?.routeIndex === index && showTrafficDropdown?.field === 'destination' && (
                      <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>
                        <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    )}
                    {showTrafficDropdown?.routeIndex === index && showTrafficDropdown?.field === 'destination' && trafficPredictions.length > 0 && (
                      <div className="location-dropdown">
                        {trafficPredictions.map((prediction) => (
                          <div
                            key={prediction.place_id}
                            className="location-dropdown-item"
                            onClick={() => handleTrafficLocationSelect(prediction, index, 'destination')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                              <path d="M21 10C21 17 12 23 12 23 C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span>{prediction.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <small>Coordinates will be saved: {route.destination_lat && route.destination_lng ? `${route.destination_lat.toFixed(4)}, ${route.destination_lng.toFixed(4)}` : 'Not set'}</small>
                </div>
              </div>
            ))}
            {formData.traffic_routes.length === 0 && (
              <p className="empty-message">No routes configured. Click "Add Route" to get started.</p>
            )}
          </div>
        )}

        {/* Credit Cards Tab */}
        {activeTab === 'cards' && (
          <div className="settings-section">
            <div className="section-header">
              <h4>Credit Cards (Max 5)</h4>
              <button
                className="btn-add"
                onClick={addCreditCard}
                disabled={formData.credit_cards.length >= 5}
              >
                <svg className="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Card
              </button>
            </div>
            {formData.credit_cards.map((card, index) => (
              <div key={index} className="card-item">
                <div className="card-header">
                  <span>Card {index + 1}</span>
                  <button className="btn-remove" onClick={() => removeCreditCard(index)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Card Name</label>
                    <input
                      type="text"
                      value={card.card_name}
                      onChange={(e) => updateCreditCard(index, 'card_name', e.target.value)}
                      placeholder="Chase Visa"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last 4 Digits</label>
                    <input
                      type="text"
                      maxLength={4}
                      value={card.last4}
                      onChange={(e) => updateCreditCard(index, 'last4', e.target.value)}
                      placeholder="1234"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={card.due_date}
                      onChange={(e) => updateCreditCard(index, 'due_date', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount Due ($)</label>
                    <input
                      type="number"
                      value={card.amount || ''}
                      onChange={(e) => updateCreditCard(index, 'amount', parseFloat(e.target.value))}
                      placeholder="150.00"
                    />
                  </div>
                </div>
              </div>
            ))}
            {formData.credit_cards.length === 0 && (
              <p className="empty-message">No cards configured. Click "Add Card" to track due dates.</p>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="settings-section">
            <h4>Quote Preferences</h4>
            <div className="form-group">
              <label>Quote Category</label>
              <select
                value={formData.quote_category}
                onChange={(e) => setFormData({ ...formData, quote_category: e.target.value as any })}
              >
                <option value="motivational">Motivational</option>
                <option value="funny">Funny</option>
                <option value="tech">Tech & Innovation</option>
                <option value="random">Random</option>
              </select>
              <small>Device will display quotes from this category</small>
            </div>
          </div>
        )}

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="settings-section">
            <h4>Daily Summary Notifications</h4>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.summary_enabled}
                  onChange={(e) => setFormData({ ...formData, summary_enabled: e.target.checked })}
                />
                Enable daily summary emails
              </label>
            </div>
            <div className="form-group">
              <label>Summary Time</label>
              <input
                type="time"
                value={formData.summary_time}
                onChange={(e) => setFormData({ ...formData, summary_time: e.target.value + ':00' })}
              />
              <small>When to send the daily summary (local time)</small>
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={formData.notification_email || ''}
                onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                placeholder="your.email@example.com"
              />
            </div>
            <div className="form-group">
              <button
                className="btn-test-summary"
                onClick={handleSendSummaryNow}
                disabled={sendingSummary || !formData.notification_email}
              >
                {sendingSummary ? (
                  <>
                    <svg className="btn-icon spinning" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 9L12 14L21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Send Test Summary Now
                  </>
                )}
              </button>
              <small>Generate and send a summary email immediately</small>
            </div>
          </div>
        )}
      </div>
    </div>
    <PopupContainer
      popup={popup}
      onClose={closePopup}
    />
  </>
  );
};

export default SettingsPanel;
